import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import {
  getColors,
  createColor,
  updateColor,
  deleteColorById,
  setCreateColor,
  setUpdateColor,
  setDeleteColorById,
} from '../../redux/actions/color';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import ColorModal from './subcomponents/ColorModal';
import { hasPermission } from '../../utils/permissions';

function ColorList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.color.get);
  const createState = useSelector((state) => state.color.create);
  const updateState = useSelector((state) => state.color.update);
  const deleteState = useSelector((state) => state.color.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingColor, setEditingColor] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getColors());
  }, [dispatch]);

  const handleCreate = useCallback(
    (name) => {
      setLoading(true);
      dispatch(createColor(name));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, name) => {
      setLoading(true);
      dispatch(updateColor(id, name));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteColorById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (color) => {
    setEditingColor(color);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingColor(null);
    setIsEditModalOpen(false);
  };

  const onColorCreate = (name) => {
    handleCreate(name);
    closeCreateModal();
  };

  const onColorEdit = (name) => {
    if (editingColor) {
      handleEdit(editingColor.id, name);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = 'BGL - Colores';
    // Solo carga inicial
    setLoading(true);
    dispatch(getColors());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getColors());
      }
      clearReduxState(setCreateColor);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getColors());
      }
      clearReduxState(setUpdateColor);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getColors());
      }
      clearReduxState(setDeleteColorById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('colors.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear color</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Colores</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center">
                  No hay colores disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>
                    <>
                      {hasPermission('colors.edit') && (
                        <Button
                          className="mr-2"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('colors.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger"
                          iconClass="fa fa-trash"
                          title="Eliminar color"
                          message="¿Estas seguro que quieres eliminar este color? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Color Modal */}
      <ColorModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear color"
        onSubmit={onColorCreate}
      />

      {/* Edit Color Modal */}
      <ColorModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar color"
        defaultValue={editingColor?.name || ''}
        onSubmit={onColorEdit}
      />
    </>
  );
}

export default ColorList;
